import { Controller } from "@hotwired/stimulus"

import { Debouncer } from "../utils/debounce.ts"

export default class extends Controller {
   static override targets = ["searchInput", "headers", "rows", "selectInput", "sortByInput", "sortDirectionInput", "pageInput", "submitter"]

   static override values = {
      selectionMode: { type: String, default: "Disabled" },
      keyscrollable: { type: Boolean, default: false },
      debounceTime: { type: Number, default: 300 },
      formId: { type: String, default: "" },
      submitOnSelect: { type: Boolean, default: false },
      // When true (Single only), clicking the selected row leaves it selected instead of clearing.
      requireSelection: { type: Boolean, default: false },
   }

   declare hasSearchInputTarget: boolean
   declare searchInputTarget: HTMLInputElement
   declare rowsTarget: HTMLElement
   declare hasSelectInputTarget: boolean
   declare selectInputTarget: HTMLSelectElement
   declare headersTarget: HTMLElement
   declare hasSortByInputTarget: boolean
   declare sortByInputTarget: HTMLInputElement
   declare hasSortDirectionInputTarget: boolean
   declare sortDirectionInputTarget: HTMLInputElement
   declare hasPageInputTarget: boolean
   declare pageInputTarget: HTMLInputElement
   declare hasSubmitterTarget: boolean
   declare submitterTarget: HTMLButtonElement

   declare selectionModeValue: "Single" | "Multiple" | "Disabled"
   declare debounceTimeValue: number
   declare keyscrollableValue: boolean
   declare formIdValue: string
   declare submitOnSelectValue: boolean
   declare requireSelectionValue: boolean

   private readonly rowSelector = ".dynamic-table-row"
   private readonly headerSelector = ".dynamic-table-header"
   private readonly cellSelector = ".dynamic-table-cell"

   private extraSelected: string[] = []
   private selectObserver: MutationObserver | null = null
   private isUpdatingSelect = false
   private readonly changeDebouncer = new Debouncer()
   private readonly searchDebouncer = new Debouncer()
   // Seeded from the rendered value at connect() so a fresh page never re-searches for what it
   // already shows. Persists across morphs (connect() does not re-run then) so a same-value `input`
   // after a replace-visit refresh still does not resubmit.
   private lastSubmittedSearch = ""

   // connect() does not re-run on a morph, so selection is re-derived from the rendered rows here.
   private readonly handleMorphElement = (event: Event): void => {
      if (event.target !== this.element) {
         return
      }

      // A person's action never fires this event, so this must never submit the form even when
      // submitOnSelect is on — only afterSelectionChanged() (never dispatchChangeEvent()) runs here.
      this.afterSelectionChanged()
   }

   get rows(): HTMLElement[] {
      return Array.from(this.rowsTarget.querySelectorAll(this.rowSelector))
   }

   public override connect(): void {
      if (this.hasSearchInputTarget) {
         this.lastSubmittedSearch = this.searchInputTarget.value
      }

      if (this.hasSelectInputTarget) {
         const selectedOptions = Array.from(this.selectInputTarget.selectedOptions)
         if (selectedOptions.length > 0) {
            const selectedValues = selectedOptions.map(opt => opt.value)
            const rows = this.rows
            for (const row of rows) {
               const rowValue = row.getAttribute("data-value")
               if (rowValue != null && selectedValues.includes(rowValue)) {
                  this.updateItemDisplay(row, true)
               }
            }

            if (this.selectionModeValue !== "Single") {
               for (const selectedValue of selectedValues) {
                  const hasRow = rows.some(row => row.getAttribute("data-value") === selectedValue)
                  if (!hasRow) {
                     this.extraSelected.push(selectedValue)
                  }
               }
            }
         } else {
            const selectedRows = this.rowsTarget.querySelectorAll(this.rowSelector + "[data-selected=true]")
            if (selectedRows.length > 0) {
               this.updateSelectedValue()
            }
         }

         if (this.selectedRows.length > 0) {
            this.scrollToRow(this.selectedRows[0]!, "auto")
         }
         this.afterSelectionChanged()

         this.selectObserver = new MutationObserver(() => {
            if (!this.isUpdatingSelect) {
               this.syncTableFromSelect()
            }
         })
         this.selectObserver.observe(this.selectInputTarget, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["selected"]
         })
      }

      this.element.addEventListener("keydown", (e: Event) => this.onKeydown(e as KeyboardEvent))
      this.element.addEventListener("turbo:morph-element", this.handleMorphElement)
   }

   public override disconnect(): void {
      this.selectObserver?.disconnect()
      this.selectObserver = null
      this.changeDebouncer.cancel()
      this.searchDebouncer.cancel()
      this.element.removeEventListener("turbo:morph-element", this.handleMorphElement)
   }

   get selectedValue() {
      return this.selectedValues.slice(-1)[0] || null
   }

   get selectedValues() {
      return Array.from(this.selectedRows, row => row.getAttribute("data-value") ?? "")
   }

   get selectedRows() {
      return this.rowsTarget.querySelectorAll(this.rowSelector + "[data-selected=true]")
   }

   dispatchChangeEvent() {
      this.changeDebouncer.run(10, () => {
         this.dispatch("change", {
            detail: {
               value: this.selectedValue,
               values: this.selectedValues,
               extra: this.extraSelected
            }
         })

         // Selection only submits when the page opts in. Otherwise it accumulates client-side and an
         // explicit submit (e.g. a Merge button) sends it. Submitting here — rather than firing a native
         // `change` on the hidden select — keeps form-controller unaware of the table and mirrors how
         // search/sort/paging already submit through submitForm.
         if (this.submitOnSelectValue) {
            this.submitForm()
         }
      })
   }

   selectByValue(value: string | number, behavior: ScrollBehavior = "smooth") {
      const rowElement = this.findRowByValue(value)

      if (!rowElement) {
         return
      }

      if (this.selectionModeValue == "Single") {
         this.updateAllItemsDisplay(false)
      }
      this.updateItemDisplay(rowElement, true)
      this.scrollToRow(rowElement, behavior)
      this.afterSelectionChanged()
   }

   selectAll() {
      this.updateAllItemsDisplay(true)
      this.afterSelectionChanged()
   }

   deselectAll() {
      this.updateAllItemsDisplay(false)
      this.afterSelectionChanged()
   }

   getSelectedRows() {
      return this.rowsTarget.querySelectorAll("[data-selected=true]")
   }

   refresh() {
      this.afterSelectionChanged()
   }

   onSearchInput() {
      if (!this.hasSearchInputTarget) {
         return
      }

      this.searchDebouncer.run(this.debounceTimeValue, () => {
         const currentSearch = this.searchInputTarget.value
         if (currentSearch === this.lastSubmittedSearch) {
            return
         }

         this.lastSubmittedSearch = currentSearch
         this.resetPage()
         this.submitForm()
      })
   }

   onHeaderClicked(event: PointerEvent) {
      if (!this.hasSortByInputTarget || !this.hasSortDirectionInputTarget) {
         return
      }

      const headerElement = (event.target as Element).closest(this.headerSelector)
      if (!headerElement || headerElement.getAttribute("sortable") === "false") {
         return
      }

      const sortKey = headerElement.getAttribute("sort-key")
      if (!sortKey) {
         return
      }

      const currentSortBy = this.sortByInputTarget.value
      const currentSortDirection = this.sortDirectionInputTarget.value

      this.sortByInputTarget.value = sortKey
      this.sortDirectionInputTarget.value = currentSortBy === sortKey && currentSortDirection === "Ascending"
         ? "Descending"
         : "Ascending"

      this.resetPage()
      this.submitForm()
   }

   onPageClicked(event: PointerEvent) {
      if (!this.hasPageInputTarget) {
         return
      }

      const button = event.currentTarget as HTMLElement | null
      const page = button?.getAttribute("data-page")
      if (!page) {
         return
      }

      this.pageInputTarget.value = page
      this.submitForm()
   }

   onRowClicked(event: PointerEvent) {
      if (this.selectionModeValue === "Disabled") {
         return
      }

      const rowElement = (event.target as Element).closest(this.rowSelector)!

      if (this.selectionModeValue === "Single") {
         if (rowElement.getAttribute("data-selected") === "true") {
            // Default Single toggles off; requireSelection keeps a Current Entry (e.g. Changelog).
            if (!this.requireSelectionValue) {
               this.updateItemDisplay(rowElement, false)
            }
         }
         else {
            this.updateAllItemsDisplay(false)
            this.updateItemDisplay(rowElement, true)
         }
      }
      else {
         if (event.ctrlKey) {
            this.toggleItemDisplay(rowElement)
         }
         else {
            this.updateAllItemsDisplay(false)
            this.extraSelected = []
            this.updateItemDisplay(rowElement, true)
         }
      }
      this.afterSelectionChanged()
      this.dispatchChangeEvent()
   }

   onRowCheckboxClicked(event: PointerEvent) {
      const rowElement = (event.target as Element).closest(this.rowSelector)!
      this.toggleItemDisplay(rowElement)
      this.afterSelectionChanged()
      this.dispatchChangeEvent()
   }

   onSelectAllChanged(event: PointerEvent) {
      const selectAllCheckboxElement = event.target as HTMLInputElement
      const selectAll = selectAllCheckboxElement.checked

      if (selectAll) {
         this.selectAll()
         this.dispatchChangeEvent()
      }
      else {
         this.deselectAll()
         this.dispatchChangeEvent()
      }
   }

   onKeydown(e: KeyboardEvent) {
      const key = e.key
      switch (key) {
         case "ArrowUp":
            if (this.selectionModeValue === "Single" && this.keyscrollableValue) {
               this.selectPrevious()
               e.preventDefault()
            }
            return
         case "ArrowDown":
            if (this.selectionModeValue === "Single" && this.keyscrollableValue) {
               this.selectNext()
               e.preventDefault()
            }
            return
      }
   }

   scrollToRow(row: Element, behavior: ScrollBehavior = "smooth") {
      const container = row.closest(".overflow-y-auto")
      if (!container) return

      const rowRect = row.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      const offset = rowRect.top - containerRect.top + container.scrollTop - container.clientHeight / 2 + rowRect.height / 2

      container.scrollTo({
         top: offset,
         behavior
      })
   }

   updateItemDisplay(row: Element, isSelected: boolean) {
      if (isSelected) {
         row.setAttribute("data-selected", "true")
      } else {
         row.removeAttribute("data-selected")
      }

      if (this.selectionModeValue === "Multiple") {
         const checkbox = row.querySelector<HTMLInputElement>("[type=checkbox]")
         if (checkbox) {
            checkbox.checked = isSelected
         }
      }

      this.updateSelectAllCheckboxState()
   }

   updateAllItemsDisplay(isSelected: boolean) {
      for (const row of this.rows) {
         this.updateItemDisplay(row, isSelected)
      }
   }

   toggleItemDisplay(row: Element) {
      this.updateItemDisplay(row, row.getAttribute("data-selected") !== "true")
   }

   afterSelectionChanged() {
      this.updateSelectAllCheckboxState()
      this.updateSelectedValue()
   }

   updateSelectAllCheckboxState() {
      const selectAllCheckboxElement = this.headersTarget.querySelector("[type=checkbox]") as HTMLInputElement

      if (this.selectionModeValue !== "Multiple" || !selectAllCheckboxElement) {
         return
      }

      const selectedRows = this.rowsTarget.querySelectorAll("[data-selected=true]")

      selectAllCheckboxElement.checked = selectedRows.length === this.rowsTarget.children.length
   }

   updateSelectedValue() {
      if (this.selectionModeValue === "Disabled" || !this.hasSelectInputTarget) {
         return
      }

      this.isUpdatingSelect = true

      const values = this.selectedValues
      const select = this.selectInputTarget

      select.innerHTML = ""
      for (const value of values) {
         const option = document.createElement("option")
         option.value = value
         option.selected = true
         option.textContent = this.getRowDisplayText(value)
         select.appendChild(option)
      }

      for (const extraValue of this.extraSelected) {
         if (!values.includes(extraValue)) {
            const option = document.createElement("option")
            option.value = extraValue
            option.selected = true
            option.textContent = extraValue
            select.appendChild(option)
         }
      }

      queueMicrotask(() => {
         this.isUpdatingSelect = false
      })
   }

   syncTableFromSelect() {
      if (!this.hasSelectInputTarget) {
         return
      }

      const selectedOptions = Array.from(this.selectInputTarget.selectedOptions)
      const selectedValues = selectedOptions.map(opt => opt.value)
      const rows = this.rows

      for (const row of rows) {
         const rowValue = row.getAttribute("data-value")
         const shouldBeSelected = rowValue != null && selectedValues.includes(rowValue)
         const isCurrentlySelected = row.getAttribute("data-selected") === "true"

         if (shouldBeSelected !== isCurrentlySelected) {
            this.updateItemDisplay(row, shouldBeSelected)
         }
      }

      if (this.selectionModeValue !== "Single") {
         this.extraSelected = []
         for (const selectedValue of selectedValues) {
            const hasRow = rows.some(row => row.getAttribute("data-value") === selectedValue)
            if (!hasRow) {
               this.extraSelected.push(selectedValue)
            }
         }
      }

      this.updateSelectAllCheckboxState()
      this.dispatchChangeEvent()
   }

   getRowDisplayText(value: string): string {
      const row = this.findRowByValue(value)
      if (row) {
         const firstCell = row.querySelector(this.cellSelector)
         return firstCell?.textContent?.trim() || value
      }
      return value
   }

   private findRowByValue(value: string | number): HTMLElement | null {
      const asString = String(value)
      return this.rows.find(row => row.getAttribute("data-value") === asString) ?? null
   }

   selectPrevious() {
      if (this.selectedRows.length === 0) {
         return
      }
      const selected = this.selectedRows[0]!
      const prev = selected.previousElementSibling as HTMLElement | null
      if (prev == null) {
         return
      }
      this.applySingleSelection(prev)
   }

   selectNext() {
      if (this.selectedRows.length === 0) {
         return
      }
      const selected = this.selectedRows[0]!
      const next = selected.nextElementSibling as HTMLElement | null
      if (next == null) {
         return
      }
      this.applySingleSelection(next)
   }

   // Stimulus binds click->dynamic-table#onRowClicked later after a morph, so a click would race.
   private applySingleSelection(row: HTMLElement) {
      if (this.selectionModeValue === "Disabled") {
         return
      }

      this.updateAllItemsDisplay(false)
      this.updateItemDisplay(row, true)
      this.afterSelectionChanged()
      this.dispatchChangeEvent()
      this.scrollToRow(row, "auto")
   }

   private resetPage() {
      if (!this.hasPageInputTarget) {
         return
      }

      this.pageInputTarget.value = "1"
   }

   // The submitter carries data-turbo-action="replace"; tables without query state render none.
   private submitForm() {
      const form = this.resolveForm()
      if (!form) {
         return
      }

      if (this.hasSubmitterTarget) {
         form.requestSubmit(this.submitterTarget)
      } else {
         form.requestSubmit()
      }
   }

   private resolveForm(): HTMLFormElement | null {
      if (this.formIdValue) {
         const form = document.getElementById(this.formIdValue)
         if (form instanceof HTMLFormElement) {
            return form
         }
      }

      return this.element.closest("form")
   }
}
